import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { apiBase } from '../utils/authHeaders';
import { errorMessage, readMktScope, requestConfig, scopeKey, useMktScope } from './MarketplaceShared';
import './Workforce.css';

export const personasApi = axios.create({ baseURL: `${apiBase()}/api/personas` });

export default function PersonaOnboarding() {
  const scope = useMktScope();
  return <PersonaWorkspace key={scopeKey(scope)} scope={scope} />;
}

export function PersonaWorkspace({ scope }) {
  const [templates, setTemplates] = useState([]);
  const [current, setCurrent] = useState(null);
  const [selected, setSelected] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    if (scope.orgId && scope.token) {
      Promise.all([
        personasApi.get('', requestConfig(scope, controller.signal)),
        personasApi.get('/current', requestConfig(scope, controller.signal)),
      ]).then(([list, config]) => {
        if (controller.signal.aborted || scopeKey(scope) !== scopeKey(readMktScope())) return;
        setTemplates(list.data.personas || []);
        setCurrent(config.data);
        setSelected(config.data.persona_id || '');
      }).catch(e => { if (!controller.signal.aborted) setError(errorMessage(e)); });
    }
    return () => controller.abort();
  }, [scope]);
  const apply = async () => {
    if (busy || !current?.can_apply || !selected) return;
    if (!window.confirm('Apply recommended persona policy? No modules will be enabled and no sites created.')) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await personasApi.post('/apply', { persona_id: selected }, requestConfig(scope));
      if (scopeKey(scope) !== scopeKey(readMktScope())) return;
      setCurrent(previous => ({ ...previous, ...res.data }));
      setNotice('Persona policy applied. Operator module enablement remains separate.');
    } catch (e) {
      if (scopeKey(scope) === scopeKey(readMktScope())) setError(errorMessage(e));
    } finally { setBusy(false); }
  };
  const template = templates.find(p => p.persona_id === selected);
  return <div className="wf-ops" data-testid="persona-onboarding">
    <h1>Business persona</h1>
    <p>Choose a recommendation pack. This does not create topology, grant permissions, or enable modules.</p>
    {error && <p role="alert">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    <label>Persona <select value={selected} onChange={e => setSelected(e.target.value)} disabled={busy}>
      <option value="">Choose a persona</option>
      {templates.map(p => <option key={p.persona_id} value={p.persona_id}>{p.description}</option>)}
    </select></label>
    {template && <section>
      <h2>Review recommendations</h2>
      <p>Node types: {(template.topology?.node_types || []).join(', ')}</p>
      <p>Modules offered (not enabled): {Object.entries(template.modules_offered || {}).filter(([, flag]) => flag === true).map(([key]) => key).join(', ')}</p>
      <p>{template.onboarding?.note}</p>
      <p>Setup prompts: {(template.onboarding?.prompts || []).join(', ') || 'Configure sites and operating policies separately.'}</p>
    </section>}
    <button type="button" onClick={apply} disabled={busy || !selected || !current?.can_apply}>Apply persona policy</button>
    {current && !current.can_apply && <p>Only organization owners and administrators can apply a persona.</p>}
  </div>;
}
